import { useEffect, useRef, useState } from "react";
import { getCachedPhotoURL, cachePhoto } from "../utility/photoCache.js";

// Stale-while-revalidate for a single photo URL: paints the last-cached copy of this
// photo instantly (if any), then fetches the live presigned URL in the background and
// swaps to it once loaded. On a fast connection the swap is invisible (image already
// loaded from cache and the fresh one loads almost immediately); on a slow connection
// (the mobile case this exists for) the old photo stays visible the whole time instead
// of a blank/spinner gap.
//
// `revalidate` (default true): when true, always re-fetches the live URL in the
// background even if a cached copy was shown immediately — right for small thumbnails
// (~5-15KB) that appear on every page load. Set to false for large images (e.g. the
// full-res lightbox view, capped at 2048px) that are only viewed occasionally and
// on-demand — those just use the cached copy if present, or fetch+cache once if not,
// without an extra background re-fetch every time they're opened.
//
// Returns the URL to put in an <img src> / background-image, or null before anything is
// available yet (caller should render its usual empty/placeholder state in that case).
export function useCachedPhoto(url, { revalidate = true } = {}) {
  const [displayUrl, setDisplayUrl] = useState(null);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setDisplayUrl(null);

    if (!url) return;

    (async () => {
      const cached = await getCachedPhotoURL(url);
      if (cancelled) {
        if (cached) URL.revokeObjectURL(cached);
        return;
      }
      if (cached) {
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = cached;
        setDisplayUrl(cached);
        if (!revalidate) return;
      } else {
        // Nothing cached yet — show the live URL directly rather than waiting.
        setDisplayUrl(url);
      }

      await cachePhoto(url);
      if (cancelled) return;
      const fresh = await getCachedPhotoURL(url);
      if (cancelled) {
        if (fresh) URL.revokeObjectURL(fresh);
        return;
      }
      if (fresh) {
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = fresh;
        setDisplayUrl(fresh);
      }
    })();

    return () => { cancelled = true; };
  }, [url, revalidate]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  return displayUrl;
}
